import { Interface, formatUnits } from "ethers";
import pusdAbi from "./pusdAbi.json";
import { ROLE_LABELS } from "./web3";

const iface = new Interface(pusdAbi);
const PUSD_DECIMALS = 6;

function fmt(raw) {
  try {
    return `${formatUnits(raw ?? 0n, PUSD_DECIMALS)} PUSD`;
  } catch (_) {
    return `${raw?.toString?.() ?? "0"} (raw)`;
  }
}

function extractRevertData(e) {
  if (!e) return null;
  if (typeof e.data === "string" && e.data.startsWith("0x") && e.data.length >= 10) return e.data;
  return (
    e?.info?.error?.data ||
    e?.error?.data ||
    e?.cause?.data ||
    e?.cause?.info?.error?.data ||
    null
  );
}

/**
 * Try to decode a contract revert into a human-readable string.
 * Returns null if the error is unrecognizable.
 */
export function decodeContractError(err) {
  // user rejected in wallet
  if (err?.code === 4001 || err?.code === "ACTION_REJECTED" || /user rejected/i.test(err?.message || "")) {
    return "Transaction rejected in your wallet.";
  }

  const data = extractRevertData(err);
  if (data) {
    try {
      const parsed = iface.parseError(data);
      if (parsed) {
        return formatErrorName(parsed.name, parsed.args);
      }
    } catch (_) {
      // unknown selector
    }
  }

  // Fallback: ethers-friendly short messages
  return err?.shortMessage || err?.reason || err?.message || "Transaction failed.";
}

function formatErrorName(name, args) {
  switch (name) {
    case "AccessControlUnauthorizedAccount": {
      const account = args?.[0];
      const role = args?.[1];
      const label = ROLE_LABELS[role] || "the required role";
      const acct = account ? `${account.slice(0, 6)}…${account.slice(-4)}` : "your wallet";
      return `${acct} does not have ${label} role. Ask the role admin to grant it (Access Control page) or, for MINTER_ROLE, ask the Master Minter to call configureMinter().`;
    }
    case "AccountBlacklisted": {
      const a = args?.[0];
      return `Address ${a?.slice(0, 6)}…${a?.slice(-4)} is blacklisted — transfers, mints, and burns to/from it are blocked.`;
    }
    case "MintExceedsAllowance": {
      const minter = args?.[0];
      const requested = args?.[1];
      const allowance = args?.[2];
      return `Mint exceeds remaining allowance. Minter ${minter?.slice(0, 6)}…${minter?.slice(-4)} requested ${fmt(requested)} but has only ${fmt(allowance)} left. Top up via the Operations → Configure Minter tab (Master Minter only).`;
    }
    case "MaxSupplyExceeded": {
      const attempted = args?.[0];
      const cap = args?.[1];
      return `Mint would push supply to ${fmt(attempted)}, above the max supply cap ${fmt(cap)}. Raise the cap (Owner) or mint less.`;
    }
    case "CapBelowSupply": {
      const cap = args?.[0];
      const supply = args?.[1];
      return `Cannot set max supply to ${fmt(cap)}: current supply (${fmt(supply)}) is already higher.`;
    }
    case "ZeroAddress":
      return "Address cannot be 0x0.";
    case "CannotRecoverSelf":
      return "Cannot recover PUSD itself via recoverERC20.";
    case "EnforcedPause":
      return "Contract is paused. The Pauser must unpause first.";
    case "ExpectedPause":
      return "Contract is not paused.";
    case "ERC20InsufficientBalance":
      return "Insufficient balance for this transfer/burn.";
    case "ERC20InsufficientAllowance":
      return "Insufficient ERC-20 allowance. The token holder must approve the caller first.";
    case "ERC20InvalidSender":
      return "Invalid sender (zero address).";
    case "ERC20InvalidReceiver":
      return "Invalid receiver (zero address).";
    default:
      return `Reverted: ${name}`;
  }
}
